import { useHover } from '@use-gesture/react';
import React, { PropsWithChildren, useRef } from 'react';
import styled, { css } from 'styled-components';
import { theme, breakpoint } from '../../../theme';
import { ConditionalWrapper, useBreakpointMap } from '../../../utils';
import { BodyText } from '../../typography/Body';
import { CategoryListItem } from './types';

const CategoryTileSelectedStyles = css`
  background-color: ${theme.colors.tints.graysRed['_2']};
  p {
    color: ${theme.colors.primary.white};
  }
  svg {
    fill: ${theme.colors.primary.white};
  }
`;

export const CategoryTileContainer = styled.div<{
  isSelected: boolean;
  cursor?: 'default' | 'pointer';
}>`
  position: relative;
  height: 80px;
  background-color: ${theme.colors.primary.white};
  border-bottom: 1px solid ${theme.colors.neutrals.solidGrays[5]};

  ${breakpoint('md')`
    height: unset;
  `}

  cursor: ${({ cursor = 'pointer' }) => cursor};
  transition: all 0.2s ease-in-out;

  svg {
    fill: ${theme.colors.primary.graysGray};
    ${breakpoint('md')`
      width: 28px;
      height: 28px;
    `}
    width: 42px;
    height: 42px;
  }

  &:hover {
    ${CategoryTileSelectedStyles}
  }

  ${({ isSelected }) => isSelected && CategoryTileSelectedStyles}
`;

export const CategoryTileLink = styled.a`
  display: flex;
  gap: ${theme.spacings[3].rem};
  align-items: center;
  padding: ${theme.spacings[3].rem} ${theme.spacings[2].rem};
  ${breakpoint('md')`
    padding: ${theme.spacings[2].rem};    
  `}
  ${breakpoint('xs')`
    padding: ${theme.spacings[3].rem} ${theme.spacings[4].rem};   
  `}
  text-decoration: none;
  color: ${theme.colors.primary.graysGray};

  width: 100%;
  height: 100%;
  box-sizing: border-box;
  &:hover{
    text-decoration: none !important;
  }
`;

export const CategoryTileImage = styled.div`
  width: 42px;
  height: 42px;
  ${breakpoint('md')`
    width: 28px;
    height: 28px;
  `}
  display: flex;
  justify-content: center;
  flex-shrink: 0;
`;

export type CategoryTileProps = {
  category: CategoryListItem;
  linkComponent?: React.FunctionComponent<PropsWithChildren<{ href: string }>>;
  isOpen: boolean;
  onClick: (categoryId: string) => void;
  onClose: (e?: MouseEvent) => void;
  hasSelectedStyles?: boolean;
};

export const CategoryTile: React.FC<CategoryTileProps> = ({
  category: { id, name, url, icon, subMenuItems },
  // eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid
  linkComponent: LinkWrapper,
  onClick,
  hasSelectedStyles = false,
}) => {
  const domTarget = useRef(null);
  const size = useBreakpointMap({ md: 'desktop' });
  const isDesktop = size === 'desktop';
  const hasSubMenuItems = subMenuItems !== undefined && subMenuItems?.length > 0 ;

  useHover(({ hovering }) => (hovering ? onClick(id) : onClick('')), {
    target: domTarget,
    enabled: isDesktop,
  });

  const useLinkComponent = LinkWrapper && (isDesktop || !hasSubMenuItems);

  return (
    <CategoryTileContainer
      key={id}
      ref={domTarget}
      isSelected={hasSelectedStyles}
    >
      <ConditionalWrapper
        condition={!!useLinkComponent}
        wrapper={(children: JSX.Element) => {
          if (!LinkWrapper) return children;
          return <LinkWrapper href={url}>{children}</LinkWrapper>;
        }}
      >
        <CategoryTileLink
          as={useLinkComponent ? undefined : 'div'}
          {...(useLinkComponent
            ? //link redirection happens onfocus, href is kept for SEO
              {
                href: url,
                onFocus: () => {
                  window.location.href = url;
                },
              }
            : {
                onClick: () => {
                  onClick(id);
                },
              })}
        >
          <>
            {/* TODO: accept both html for svg, and SVGComponent? */}
            <CategoryTileImage dangerouslySetInnerHTML={{ __html: icon }} />
            <BodyText size={{ xs: 'md', md: 'sm' }}>{name}</BodyText>
          </>
        </CategoryTileLink>
      </ConditionalWrapper>

      
    </CategoryTileContainer>
  );
};
